import { useEffect, useState } from 'react';
import axios from 'axios';
import { observer, inject } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import MileageForm from '../pages/new-expenses-mobile/mileage-form';
import { officeNameWithAliasesLabel } from '../utils/office-option-label';
import { imputationDocumentId } from '../pages/review-expense/utils/contract-expense-json';
import { travelTypeRequiresMatter, mileageSaveErrorKey } from '../utils/mileage-validation';
import {
  useAccessibleTravelTypeIds,
  filterToAccessibleTravelTypes,
} from '../utils/accessible-travel-types';
import Checkbox from './checkbox';
import Checkmark from './checkmark';
import Modal from './modal';
import { fetch } from '../utils/api-wrapper';
import ListHeader from './list-header';
import ListItemDisplay from './list-item-display';
import AccessControl from '../utils/access_control';
import { canValidateExpense, isAccountOpenForValidation } from '../pages/review-expense/utils/expense-review-list';

const isMileageExpense = (expense) => expense.json?.type === 'mileage';

const MileageList = ({
  data,
  expensesAccount,
  saving,
  setCurrentExpense,
  setIsNewExpenseCreation,
  setSaving,
  fetchExpensesAccount,
  approveExpense,
  disApproveExpense,
  validateExpense,
}) => {
  // Two separate questions: may this person validate, and is this report far
  // enough along to be validated at all (XPEN-3383).
  const canValidate = AccessControl.userCanValidateExpenses(data) && isAccountOpenForValidation(expensesAccount);
  const todayISO = new Date().toISOString().split('T')[0];

  // Single form state shared by desktop and mobile (XPEN-2721).
  const initialMileageForm = {
    travelTypeId: '',
    departure: '',
    destination: '',
    roundTrip: false,
    matterId: '',
    workDate: todayISO,
    description: '',
    openAmount: '',
  };

  const [showMileageForm, setShowMileageForm] = useState(false);
  const [mileageForm, setMileageForm] = useState(initialMileageForm);
  const source = axios.CancelToken.source();
  const [xpensesDeleteSelection, setXpensesDeleteSelection] = useState([]);
  const [xpensesDeleteConfirm, setXpensesDeleteConfirm] = useState(false);
  const [travelTypes, setTravelTypes] = useState([]);
  const [travelTypesLoading, setTravelTypesLoading] = useState(true);
  const [travelTypesError, setTravelTypesError] = useState(false);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [mileageSaveError, setMileageSaveError] = useState(null);
  // XPEN-3481: an administrator can reserve a travel type for a department or a
  // rank. The rates configuration carries every type, so the allowed ids are
  // fetched alongside it and used to narrow what is offered for this report —
  // judged on its owner, who is not always the person at the keyboard.
  const {
    allowedIds: accessibleTravelTypeIds,
    loading: accessibleTravelTypeIdsLoading,
  } = useAccessibleTravelTypeIds(expensesAccount?.user?.id_user);
  useEffect(() => {
    fetch(
      '/expense_rates/',
      'GET',
      {},
      response => {
        const allTypes = response.data?.rates?.mileage_travel_types ?? [];
        setTravelTypes(allTypes.filter(tt => tt.active));
        setTravelTypesLoading(false);
      },
      () => { setTravelTypesError(true); setTravelTypesLoading(false); }
    );
  }, []);
  const selectableTravelTypes = filterToAccessibleTravelTypes(travelTypes, accessibleTravelTypeIds);

  const { account_id } = useParams();
  const mileageExpenses = expensesAccount?.expenses?.filter(isMileageExpense) ?? [];
  const isAllXpensesSelected = !!(
    expensesAccount &&
    expensesAccount.expenses &&
    xpensesDeleteSelection.length > 1 &&
    xpensesDeleteSelection.length === mileageExpenses.length
  );
  const toggleAllXpensesSelected = () => {
    xpensesDeleteSelection.length === mileageExpenses.length
      ? setXpensesDeleteSelection([])
      : setXpensesDeleteSelection(mileageExpenses.map((x) => x.checksum));
  };

  const isXpenseSelected = (xpens) => {
    return xpensesDeleteSelection.find((x) => x === xpens.checksum) !== undefined;
  };

  const deleteXpenses = () => {
    fetch(
      `/expenses_account/expenses/${account_id}`,
      'DELETE',
      { checksums: xpensesDeleteSelection },
      () => {
        fetchExpensesAccount();
        setXpensesDeleteSelection([]);
        setXpensesDeleteConfirm(false);
      },
      null,
      false,
      source.token
    );
  };

  // Single save function for both desktop and mobile.
  // Fires two PUTs: first creates the shell, second adds route data so the API
  // can calculate distance and total before the expense list refreshes.
  const addMileageExpense = () => {
    setSaving(true);
    setMileageSaveError(null);
    const selectedTravelType = travelTypes.find(tt => tt.id === mileageForm.travelTypeId);
    const isOfficeTravel = selectedTravelType?.travel_mode === 'office_travel';
    // XPEN-3246-009: "not office" no longer means "address" — a third mode is
    // priced by a typed amount, with no route at all.
    const isOpenAmountTravel = selectedTravelType?.travel_mode === 'open_amount';
    const isBillable = selectedTravelType?.billable_scope === 'billable';
    const matterId = travelTypeRequiresMatter(selectedTravelType) ? String(mileageForm.matterId ?? '').trim() : '';

    const formData = new FormData();
    formData.append('travel_type_id', mileageForm.travelTypeId);
    formData.append('description', mileageForm.description);
    if (matterId) {
      formData.append('matter_id', matterId);
    }

    fetch(
      `/expenses_account/add/${account_id}`,
      'PUT',
      formData,
      (shellResponse) => {
        const shell = shellResponse.data;
        const firstListItem = shell?.json?.ListItem?.[0] ?? {};
        const departureOffice = officeOptions.find(o => o.value === mileageForm.departure);
        const destinationOffice = officeOptions.find(o => o.value === mileageForm.destination);

        const routeJson = {
          ...shell.json,
          // Explicit even though state already resets it — this mode has no
          // route, so the payload must not depend on state being in sync.
          isRoundTrip: isOpenAmountTravel ? false : mileageForm.roundTrip,
          ...(matterId ? { MatterId: matterId } : {}),
          ...(isBillable ? { IsBillable: true } : {}),
          ListItem: [{
            ...firstListItem,
            WorkDate: mileageForm.workDate,
            Description: mileageForm.description,
            ...(matterId ? { MatterId: matterId } : {}),
            ...(isBillable ? { IsBillable: 1 } : {}),
          }],
          ...(isOfficeTravel
            ? {
                departure_office_id: mileageForm.departure,
                destination_office_id: mileageForm.destination,
                AddressOrigin: departureOffice?.address || '',
                AddressDestination: destinationOffice?.address || '',
              }
            : isOpenAmountTravel
            ? {
                // No address fields at all for this mode — the trip has no route.
                open_amount: Number(mileageForm.openAmount),
              }
            : {
                AddressOrigin: mileageForm.departure,
                AddressDestination: mileageForm.destination,
              }
          ),
        };

        fetch(
          `/expenses/${account_id}/${shell.checksum}`,
          'PUT',
          { ...shell, json: routeJson },
          () => {
            setSaving(false);
            setShowMileageForm(false);
            setMileageForm(initialMileageForm);
            fetchExpensesAccount();
          },
          // The trip already exists by this point — the first PUT created it. Saying
          // "could not be saved" here would be untrue, and would invite a retry that
          // creates a second trip. Refresh so the half-finished one is at least
          // visible in the list behind the form.
          (error) => {
            setSaving(false);
            setMileageSaveError(
              data.t.read(mileageSaveErrorKey(error, 'error_mileage_details_save_failed'))
            );
            fetchExpensesAccount();
          }
        );
      },
      (error) => { setSaving(false); setMileageSaveError(data.t.read(mileageSaveErrorKey(error))); }
    );
  };

  const loadOffices = () => {
    if (officeOptions.length > 0 || officesLoading) return;
    setOfficesLoading(true);
    const filters = [
      { id: 'id_client', value: data.id_client },
      { id: 'is_place_of_business', value: true },
    ];
    const sorting = [{ id: 'name', desc: false }];
    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify(sorting))}&start=0&page_size=500`;
    fetch(`/offices/${searchData}`, 'GET', {}, response => {
      const offices = response.data?.offices ?? [];
      setOfficeOptions(offices.map(o => ({
        value: o._id?.$oid ?? String(o._id),
        label: officeNameWithAliasesLabel(o),
        address: o.address ?? '',
      })));
      setOfficesLoading(false);
      // Pre-fill departure with the user's home office when office_travel is selected (mirrors expense.js behaviour).
      // Guard against the type having changed while this GET was in flight
      // (e.g. switched to Open Amount before offices loaded) — XPEN-3426 review.
      setMileageForm(prev => {
        if (prev.departure) return prev;
        const currentTT = travelTypes.find(tt => tt.id === prev.travelTypeId);
        if (currentTT?.travel_mode !== 'office_travel') return prev;
        const profileHomeRaw = expensesAccount?.user?.home_office_id ?? data.home_office_id;
        if (!profileHomeRaw) return prev;
        const homeId = imputationDocumentId({ _id: profileHomeRaw });
        const homeOpt = offices.find(o => imputationDocumentId({ _id: o._id }) === homeId);
        if (!homeOpt) return prev;
        return { ...prev, departure: homeOpt._id?.$oid ?? String(homeOpt._id) };
      });
    }, () => setOfficesLoading(false));
  };

  const updateMileageFormField = (key, value) => {
    setMileageForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'travelTypeId') {
        next.departure = '';
        next.destination = '';
        // A stale typed amount from a previous selection must not survive a
        // travel-type change, the same reason departure/destination reset above.
        next.openAmount = '';
        // A stale round-trip toggle must not survive either (XPEN-3426 review).
        next.roundTrip = false;
        const newTT = travelTypes.find(tt => tt.id === value);
        if (newTT?.travel_mode === 'office_travel') {
          if (officeOptions.length > 0) {
            // Offices already loaded — pre-fill departure immediately.
            const profileHomeRaw = expensesAccount?.user?.home_office_id ?? data.home_office_id;
            if (profileHomeRaw) {
              const homeId = imputationDocumentId({ _id: profileHomeRaw });
              const homeOpt = officeOptions.find(o => o.value === homeId);
              if (homeOpt) next.departure = homeOpt.value;
            }
          } else {
            // First time — load offices; callback handles pre-fill.
            loadOffices();
          }
        }
      }
      return next;
    });
  };

  return (
    <>
      {/* MileageForm overlay — centered on both axes on all screen sizes (XPEN-2721) */}
      {showMileageForm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1050,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflowY: 'auto',
            padding: data.isMobile ? '1rem' : 0,
          }}
          data-testid="mileage-form-overlay"
        >
          <div style={{
            width: data.isMobile ? '100%' : '90%',
            maxWidth: '680px',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <div className="modal-content">
              {mileageSaveError && (
                <div className="alert alert-danger m-3 mb-0" role="alert" data-testid="mileage-save-error">
                  {mileageSaveError}
                </div>
              )}
              <MileageForm
                data={data}
                form={mileageForm}
                isMobile={!!data.isMobile}
                travelTypes={selectableTravelTypes}
                travelTypesLoading={travelTypesLoading || accessibleTravelTypeIdsLoading}
                travelTypesError={travelTypesError}
                officeOptions={officeOptions}
                officesLoading={officesLoading}
                onFieldChange={updateMileageFormField}
                onSubmit={addMileageExpense}
                onCancel={() => {
                  setShowMileageForm(false);
                  setMileageForm(initialMileageForm);
                  setMileageSaveError(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <Accordion
        defaultExpanded
        className={'rounded-lg shadow-none border mb-3'}
        sx={{ '&:before': { display: 'none' } }}
        disableGutters
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="xpenses-panel-content"
          id="xpenses-panel-header"
          className={'bg-light rounded-lg'}
        >
          <h3>{data.t.read('mileage')}</h3>
        </AccordionSummary>
        <AccordionDetails>
          {data.isMobile ? (
            <>
              <ListHeader
                checkboxDisabled={!expensesAccount || !expensesAccount.expenses}
                isChecked={isAllXpensesSelected}
                onCheckboxChange={toggleAllXpensesSelected}
                hideDeleteButton={expensesAccount?.status !== 'Pending'}
                disableDeleteButton={saving || xpensesDeleteSelection.length === 0}
                onDelete={() => setXpensesDeleteConfirm(true)}
                hideAddButton={expensesAccount?.status !== 'Pending'}
                disableAddButton={saving || expensesAccount?.status !== 'Pending'}
                onAdd={() => setShowMileageForm(true)}
                saving={saving}
                data={data}
              />
              {mileageExpenses.map((xpens) => (
                <ListItemDisplay
                  key={xpens.checksum}
                  isSelected={isXpenseSelected(xpens)}
                  onSelectedChange={() =>
                    isXpenseSelected(xpens)
                      ? setXpensesDeleteSelection(xpensesDeleteSelection.filter((x) => x !== xpens.checksum))
                      : setXpensesDeleteSelection([...xpensesDeleteSelection, xpens.checksum])
                  }
                >
                  <div
                    className="ml-3 text-truncate pointer flex-1"
                    onClick={() => {
                      if (setIsNewExpenseCreation) setIsNewExpenseCreation(false);
                      setCurrentExpense(xpens);
                    }}
                  >
                    {data.t.read(xpens.json?.type)}
                  </div>
                  <div className="text-primary font-weight-bold ml-2">
                    {xpens.json && xpens.json?.BigTotalPayed && `$${xpens.json.BigTotalPayed.toFixed(2)}`}
                  </div>
                  <div className="text-right text-muted flex-shrink-0" style={{ width: '96px' }}>
                    {xpens.json?.InvoiceDate}
                  </div>
                  {canValidate && (
                    <div className="ml-2 flex-shrink-0">
                      <Checkmark
                        isChecked={xpens.status === 'Validated'}
                        onChecked={() => validateExpense(xpens)}
                        disabled={!canValidateExpense(xpens.status)}
                        callback={() => fetchExpensesAccount()}
                        iconProps={{
                          'data-testid': `validate-checkmark-${xpens.checksum}`,
                          role: 'checkbox',
                          'aria-checked': xpens.status === 'Validated',
                          'aria-label': data.t.read('validate'),
                        }}
                      />
                    </div>
                  )}
                  {canValidate && (
                    <div className="text-right text-muted flex-shrink-0 ml-2" style={{ width: '96px' }}>
                      {xpens.status ? data.t.read(`status.${xpens.status.toLowerCase()}`) : ''}
                    </div>
                  )}
                </ListItemDisplay>
              ))}
            </>
          ) : (
            <div className="d-flex">
              <div className="col-8">
                <table className="table table-borderless table-sm">
                  <thead>
                    <tr>
                      <th>
                        <Checkbox
                          disabled={!expensesAccount || !expensesAccount.expenses}
                          isChecked={isAllXpensesSelected}
                          onChange={toggleAllXpensesSelected}
                        />
                      </th>
                      <th className="text-center col-4">
                        {data.t.read('expense')} {data.t.read('type')}
                      </th>
                      <th className="text-center col-4">
                        {data.t.read('expense')} {data.t.read('invoiceDate')}
                      </th>
                      <th className="text-center col-4">{data.t.read('invoice.total')}</th>
                      {canValidate && <th className="text-center col-2">{data.t.read('validate')}</th>}
                      {canValidate && <th className="text-center col-2">{data.t.read('validation_status')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {mileageExpenses.map((xpens, index) => (
                      <tr key={index}>
                        <td>
                          <Checkbox
                            isChecked={isXpenseSelected(xpens)}
                            onChange={() =>
                              isXpenseSelected(xpens)
                                ? setXpensesDeleteSelection(xpensesDeleteSelection.filter((x) => x !== xpens.checksum))
                                : setXpensesDeleteSelection([...xpensesDeleteSelection, xpens.checksum])
                            }
                          />
                        </td>
                        <td
                          className="text-center"
                          onClick={() => {
                            if (setIsNewExpenseCreation) setIsNewExpenseCreation(false);
                            setCurrentExpense(xpens);
                          }}
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: 'blue' }}
                        >
                          {data.t.read(xpens.json?.type)}
                        </td>
                        <td className="text-center">{xpens.json?.InvoiceDate}</td>
                        <td className="text-center">
                          {xpens.json && xpens.json?.BigTotalPayed && `$${xpens.json.BigTotalPayed.toFixed(2)}`}
                        </td>
                        {canValidate && (
                          <td className="text-center">
                            <Checkmark
                              isChecked={xpens.status === 'Validated'}
                              onChecked={() => validateExpense(xpens)}
                              disabled={!canValidateExpense(xpens.status)}
                              callback={() => fetchExpensesAccount()}
                              iconProps={{
                                'data-testid': `validate-checkmark-${xpens.checksum}`,
                                role: 'checkbox',
                                'aria-checked': xpens.status === 'Validated',
                                'aria-label': data.t.read('validate'),
                              }}
                            />
                          </td>
                        )}
                        {canValidate && (
                          <td className="text-center">
                            {xpens.status ? data.t.read(`status.${xpens.status.toLowerCase()}`) : ''}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="col-4 text-right">
                <button
                  hidden={expensesAccount?.status !== 'Pending'}
                  type="button"
                  className="mr-1 pointer btn btn-sm btn-secondary btn-primary-shadow pb-2 pt-2"
                  disabled={saving || xpensesDeleteSelection.length === 0}
                  onClick={() => setXpensesDeleteConfirm(true)}
                >
                  <div>
                    {data.t.read('delete')}
                    {saving && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                  </div>
                </button>
                <button
                  hidden={expensesAccount?.status !== 'Pending'}
                  type="button"
                  className="btn btn-sm btn-primary btn-primary-shadow pb-2 pt-2"
                  disabled={saving || expensesAccount?.status !== 'Pending'}
                  onClick={() => setShowMileageForm(true)}
                >
                  <div className="action-buttons action-buttons-sm">
                    {data.t.read('add_new')}
                    {saving && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                  </div>
                </button>
              </div>
            </div>
          )}
        </AccordionDetails>
      </Accordion>

      {xpensesDeleteConfirm === true && (
        <Modal
          title={data.t.read('confirm')}
          cancelAction={() => setXpensesDeleteConfirm(false)}
          okAction={() => deleteXpenses()}
          okText={data.t.read('delete')}
          cancelText={data.t.read('cancel')}
        >
          {data.t.read('delete')} ?
        </Modal>
      )}
    </>
  );
};

export default inject('data')(observer(MileageList));
